import React from 'react';
import { Box, Typography, TextField, Slider } from '@mui/material';

const PriceRangeSlider = ({ minPrice, maxPrice, value, onChange }) => {
    // Local state for visual drag — doesn't trigger API
    const [localRange, setLocalRange] = React.useState(value || [minPrice, maxPrice]);
    const isDragging = React.useRef(false);

    // Sync with parent when not dragging
    React.useEffect(() => {
        if (!isDragging.current) {
            setLocalRange(value || [minPrice, maxPrice]);
        }
    }, [value, minPrice, maxPrice]);

    const priceRange = localRange;

    // Visual update while dragging — no API call
    const handleSliderChange = (event, newValue) => {
        isDragging.current = true;
        setLocalRange(newValue);
    };

    // Mouse released — trigger API call
    const handleSliderCommit = (event, newValue) => {
        isDragging.current = false;
        onChange(newValue);
    };

    const handleMinInputChange = (e) => {
        const newMin = Number(e.target.value);
        if (newMin >= minPrice && newMin <= priceRange[1]) {
            setLocalRange([newMin, priceRange[1]]);
            onChange([newMin, priceRange[1]]);
        }
    };

    const handleMaxInputChange = (e) => {
        const newMax = Number(e.target.value);
        if (newMax <= maxPrice && newMax >= priceRange[0]) {
            setLocalRange([priceRange[0], newMax]);
            onChange([priceRange[0], newMax]);
        }
    };

    return (
        <Box>
            <Box sx={{ px: 1, mb: 2 }}>
                <Slider
                    value={priceRange}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                    valueLabelDisplay="auto"
                    min={minPrice}
                    max={maxPrice}
                    valueLabelFormat={(val) => `$${val.toLocaleString()}`}
                    sx={{
                        color: '#801F56',
                        height: 4,
                        '& .MuiSlider-thumb': {
                            width: 16,
                            height: 16,
                            backgroundColor: '#fff',
                            border: '2px solid #801F56',
                            '&:hover, &.Mui-focusVisible': {
                                boxShadow: '0 0 0 6px rgba(128, 31, 86, 0.1)',
                            },
                            '&.Mui-active': {
                                boxShadow: '0 0 0 10px rgba(128, 31, 86, 0.1)',
                            },
                        },
                        '& .MuiSlider-track': {
                            border: 'none',
                        },
                        '& .MuiSlider-rail': {
                            backgroundColor: '#ddd',
                            opacity: 1,
                        },
                        '& .MuiSlider-valueLabel': {
                            backgroundColor: '#111',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        },
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                    type="number"
                    value={priceRange[0]}
                    onChange={handleMinInputChange}
                    size="small"
                    InputProps={{
                        startAdornment: <span style={{ color: '#888', marginRight: 4, fontSize: '0.9rem' }}>$</span>,
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            '& input': {
                                fontSize: '0.9rem',
                                fontWeight: 500,
                            },
                            '& fieldset': {
                                borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                                borderColor: '#801F56',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#801F56',
                            },
                        },
                    }}
                />

                <Typography sx={{ color: '#999', fontSize: '0.9rem' }}>—</Typography>

                <TextField
                    type="number"
                    value={priceRange[1]}
                    onChange={handleMaxInputChange}
                    size="small"
                    InputProps={{
                        startAdornment: <span style={{ color: '#888', marginRight: 4, fontSize: '0.9rem' }}>$</span>,
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            '& input': {
                                fontSize: '0.9rem',
                                fontWeight: 500,
                            },
                            '& fieldset': {
                                borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                                borderColor: '#801F56',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#801F56',
                            },
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default PriceRangeSlider;
